import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useForm } from 'react-hook-form';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import { BillingKeys, createHubspotContact } from '../../../api/billing-api';
import { HubSpotContact } from '../../../api/billing-types';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import BillingAddressForm from './billing-address-form';
import ContactInfoForm from './contact-info-form';
import ShippingAddressForm from './shipping-address-form';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
interface AddPaymentContactFormProps {
  open: boolean;
  handleClose: () => void;
  hubSpotCompanyId: number;
  changeLogRoles: string[];
  billingRoles: string[];
  setOpenAddContact: Dispatch<SetStateAction<boolean>>;
  hubSpotContacts: HubSpotContact[];
}

export default function AddBillingContactDialog({
  open,
  handleClose,
  hubSpotCompanyId,
  changeLogRoles,
  billingRoles,
  setOpenAddContact,
  hubSpotContacts,
}: AddPaymentContactFormProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [matchBillingAddress, setMatchBillingAddress] = useState(true);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm<Partial<HubSpotContact>>({
    defaultValues: {
      properties: {
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        // billing address fields
        address: '',
        street_address_2: '',
        city: '',
        state: '',
        country: 'US',
        zip: '',
        // shipping address fields
        street_address_1__shipping_: '',
        street_address_2__shipping_: '',
        city__shipping_: '',
        state__shipping_: '',
        country__shipping_: 'US',
        postal_code__shipping_: '',
        primary_billing_contact_: false,
        billing_contact_: true,
      },
    },
  });

  const onSubmit = (data: Partial<HubSpotContact>) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, BillingRoles.canAddContacts.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<HubSpotContact>) => {
      if (matchBillingAddress) {
        data.properties!.street_address_1__shipping_ = data.properties!.address;
        data.properties!.street_address_2__shipping_ =
          data.properties!.street_address_2;
        data.properties!.city__shipping_ = data.properties!.city;
        data.properties!.state__shipping_ = data.properties!.state;
        data.properties!.postal_code__shipping_ = data.properties!.zip;
        data.properties!.country__shipping_ = data.properties!.country;
      }

      return createHubspotContact(data, hubSpotCompanyId, changeLogMessage);
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Billing contact added successfully!',
      });

      handleClose();

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.HubSpotContacts, hubSpotCompanyId],
      });

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the billing contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.billing_contacts.create_billing_contact, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.billingContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      maxWidth="lg"
      title="Add Billing Contact"
      dialogContent={
        <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
          <ContactInfoForm
            control={control}
            errors={errors}
            billingRoles={billingRoles}
            formType="create"
            hubSpotCompanyId={hubSpotCompanyId}
            hubSpotContacts={hubSpotContacts}
            trigger={trigger}
            watch={watch}
            setOpenAddContact={setOpenAddContact}
          />

          <Divider sx={{ my: 2 }} />

          <BillingAddressForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            billingRoles={billingRoles}
            formType="create"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={matchBillingAddress}
                onClick={() => {
                  if (matchBillingAddress) {
                    setValue('properties.street_address_1__shipping_', '');
                    setValue('properties.street_address_2__shipping_', '');
                    setValue('properties.city__shipping_', '');
                    setValue('properties.state__shipping_', '');
                    setValue('properties.postal_code__shipping_', '');
                    setValue('properties.country__shipping_', '');

                    setMatchBillingAddress(false);
                  } else {
                    setValue(
                      'properties.street_address_1__shipping_',
                      getValues('properties.address')
                    );
                    setValue(
                      'properties.street_address_2__shipping_',
                      getValues('properties.street_address_2')
                    );
                    setValue(
                      'properties.city__shipping_',
                      getValues('properties.city')
                    );
                    setValue(
                      'properties.state__shipping_',
                      getValues('properties.state')
                    );
                    setValue(
                      'properties.postal_code__shipping_',
                      getValues('properties.zip')
                    );
                    setValue(
                      'properties.country__shipping_',
                      getValues('properties.country')
                    );

                    setMatchBillingAddress(true);
                  }
                }}
              />
            }
            label="Shipping address matches billing address"
          />

          {!matchBillingAddress && (
            <>
              <Divider sx={{ my: 2 }} />
              <ShippingAddressForm
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                billingRoles={billingRoles}
                formType="create"
              />
            </>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate(getValues());
            }}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled:
          isPending ||
          !isDirty ||
          (errors && errors.properties?.email?.type === 'validate'),
        title: 'Add Contact',
      }}
    />
  );
}
